import { HugeiconsIcon, IconSvgElement } from "@hugeicons/react";
import { Box, CircularProgress } from "@mui/material";
import dynamic from "next/dynamic";
import Image from "next/image";
import { ComponentType, CSSProperties } from "react";
import LinkOffCircle from "@/public/assets/link-off-circle.svg";

export type Icon = {
  color?: string;
  size?: number | string;
  strokeWidth?: number | string;
  style?: CSSProperties;
  set?: "broken" | "curved" | "duotone" | "outline";
  onClick?: () => void;
};

// Icon consumer component
function createHugeIconComponent(icon: IconSvgElement | undefined) {
  const IconComponent = ({
    color,
    size = 20,
    strokeWidth,
    style,
    ...rest
  }: Icon) => (
    <HugeiconsIcon
      size={size}
      icon={icon}
      strokeWidth={
        typeof strokeWidth === "string" ? parseFloat(strokeWidth) : strokeWidth
      }
      color={color}
      style={style}
      {...rest}
    />
  );
  // If the icon is not found, return a placeholder icon
  return icon ? IconComponent : PlaceholderIcon;
}

const PlaceholderIcon = () => (
  <Image
    src={LinkOffCircle}
    width={20}
    height={20}
    alt="Placeholder missing icon"
  />
);

const LoadingIcon = () => (
  <Box
    sx={{
      width: 20,
      height: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <CircularProgress size={16} />
  </Box>
);

// Helper function to safely access icon from module
function getIconFromModule(mod: Record<string, unknown>, iconName: string) {
  const icons = mod.default ?? mod;
  return (
    (icons as Record<string, IconSvgElement | undefined>)[iconName] ?? undefined
  );
}

// Helper functions for each icon package with static import paths
function createStandardIcon(iconName: string) {
  return dynamic(
    () =>
      import("@hugeicons-pro/core-stroke-standard").then((mod) =>
        createHugeIconComponent(getIconFromModule(mod, iconName))
      ),
    {
      ssr: false,
    }
  );
}

export function createBulkRoundedIcon(iconName: string, withLoading?: boolean) {
  return dynamic(
    () =>
      import("@hugeicons-pro/core-bulk-rounded").then((mod) =>
        createHugeIconComponent(getIconFromModule(mod, iconName))
      ),
    {
      ssr: false,
      loading: withLoading ? () => <LoadingIcon /> : () => <></>,
    }
  );
}

function createSolidStandardIcon(iconName: string, withLoading?: boolean) {
  return dynamic(
    () =>
      import("@hugeicons-pro/core-solid-standard").then((mod) =>
        createHugeIconComponent(getIconFromModule(mod, iconName))
      ),
    {
      ssr: false,
      loading: withLoading ? () => <CircularProgress size={16} /> : () => <></>,
    }
  );
}

// Replace createDynamicIcon with specific helper functions
export const ChevronSmallDownIcon = createStandardIcon("ArrowDown01Icon");

export const CalendarIcon = createStandardIcon("Calendar04Icon");

export const CalendarClockIcon = createStandardIcon("DateTimeIcon");

export const CalendarCheckIcon = createStandardIcon("Appointment01Icon");

export const CalendarCrossIcon = createStandardIcon("CalendarRemove01Icon");

export const CalendarUserIcon = createStandardIcon("CalendarUserIcon");

export const UsersIcon = createStandardIcon("UserMultipleIcon");

export const UserIcon = createStandardIcon("UserIcon");

export const UserPlusIcon = createStandardIcon("UserAdd02Icon");

export const SettingsIcon = createStandardIcon("Settings01Icon");

export const PackageIcon = createStandardIcon("DeliveryBox02Icon");

export const QuestionCircleIcon = createStandardIcon("HelpCircleIcon");

export const ReportsIcon = createStandardIcon("Analytics01Icon");

export const TrendingUpIcon = createStandardIcon("TradeUpIcon");

export const CoinIcon = createStandardIcon("DollarCircleIcon");

export const TagIcon = createStandardIcon("Tag01Icon");

export const MenuHorizontalIcon = createStandardIcon("MoreHorizontalIcon");

export const OfferIcon = createStandardIcon("DiscountIcon");

export const ChevronSmallUpIcon = createStandardIcon("ArrowUp01Icon");

export const ChevronSmallRightIcon = createStandardIcon("ArrowRight01Icon");

export const ChevronSmallLeftIcon = createStandardIcon("ArrowLeft01Icon");

export const ArrowLeftIcon = createStandardIcon("ArrowLeft02Icon");

export const ArrowRightIcon = createStandardIcon("ArrowRight02Icon");

export const ArrowSmallUpIcon = createStandardIcon("ArrowUp02Icon");

export const PlusCircleIcon = createStandardIcon("AddCircleIcon");

export const MinusCircleIcon = createStandardIcon("RemoveCircleIcon");

export const CrossCircleIcon = createStandardIcon("CancelCircleIcon");

export const MedsIcon = createStandardIcon("MedicineBottleIcon");

export const MedkitIcon = createStandardIcon("FirstAidKitIcon");

export const DocumentIcon = createStandardIcon("File02Icon");

export const SearchIcon = createStandardIcon("Search01Icon");

export const PhoneIcon = createStandardIcon("Call02Icon");

export const ListRightIcon = createStandardIcon("LicenseIcon");

export const PinpaperCrossIcon = createStandardIcon("TaskRemove01Icon");

export const PinpaperFilledIcon = createStandardIcon("Task01Icon");

export const UserWarningIcon = createStandardIcon("UserWarning03Icon");

export const TrashIcon = createStandardIcon("Delete02Icon");

export const CheckIcon = createStandardIcon("Tick02Icon");

export const CrossIcon = createStandardIcon("Cancel01Icon");

export const PlusIcon = createStandardIcon("Add01Icon");

export const EditIcon = createStandardIcon("Edit03Icon");

export const EditTemplateIcon = createStandardIcon("Edit02Icon");

export const HomeIcon = createStandardIcon("Home11Icon");

export const MessageIcon = createStandardIcon("Message01Icon");

export const MessageSquareIcon = createStandardIcon("Comment01Icon");

export const CheckCircleIcon = createStandardIcon("CheckmarkCircle02Icon");

export const UploadIcon = createStandardIcon("CloudUploadIcon");

export const EyeIcon = createStandardIcon("ViewIcon");

export const EyeClosedIcon = createStandardIcon("ViewOffSlashIcon");

export const SendIcon = createStandardIcon("SentIcon");

export const ImageIcon = createStandardIcon("Image01Icon");

export const WarningCircleIcon = createStandardIcon("AlertCircleIcon");

export const InjectionIcon = createStandardIcon("InjectionIcon");

export const InfoCircleIcon = createStandardIcon("InformationCircleIcon");

export const CreditCardIcon = createStandardIcon("CreditCardIcon");

export const WalletIcon = createStandardIcon("Wallet01Icon");

export const SaveIcon = createStandardIcon("FloppyDiskIcon");

export const DownloadIcon = createStandardIcon("Download05Icon");

export const ShareIcon = createStandardIcon("Share08Icon");

export const SpeakerIcon = createStandardIcon("VolumeLowIcon");

export const GridIcon = createStandardIcon("DashboardSquare01Icon");

export const MailIcon = createStandardIcon("Mail01Icon");

export const CartIcon = createStandardIcon("ShoppingCart01Icon");

export const ShoppingBagIcon = createStandardIcon("ShoppingBag02Icon");

export const PlayIcon = createStandardIcon("PlayIcon");

export const Location1Icon = createStandardIcon("Location01Icon");

export const ClockIcon = createStandardIcon("Clock01Icon");

export const AlarmIcon = createStandardIcon("AlarmClockIcon");

export const AlarmDuotoneIcon = createBulkRoundedIcon("AlarmClockIcon");

export const CrownIcon = createStandardIcon("Crown03Icon");

export const CardiologyIcon = createStandardIcon("Cardiogram02Icon");

export const CamIcon = createStandardIcon("Video01Icon");

export const CameraIcon = createStandardIcon("Camera01Icon");

export const CapsuleIcon = createStandardIcon("Medicine02Icon");

export const MinusSmallIcon = createStandardIcon("Remove01Icon");

export const LogoutIcon = createStandardIcon("Logout03Icon");

export const MenuHamburgerIcon = createStandardIcon("Menu01Icon");

export const UserQuestionIcon = createStandardIcon("UserQuestion02Icon");

export const NewScreenIcon = createStandardIcon("LinkSquare02Icon");

export const ShieldUserIcon = createStandardIcon("ShieldUserIcon");

export const HomeHospitalIcon = createStandardIcon("ClinicIcon");

export const WarningIcon = createStandardIcon("Alert02Icon");

export const AttachmentIcon = createStandardIcon("Attachment02Icon");

export const BellIcon = createStandardIcon("NotificationIcon");

export const GiftIcon = createStandardIcon("GiftIcon");

export const PlasterIcon = createStandardIcon("BandageIcon");

export const CopyIcon = createStandardIcon("Copy01Icon");

export const UsersDuotoneIcon = createBulkRoundedIcon("UserMultiple02Icon");

export const PhoneDuotoneIcon = createBulkRoundedIcon("Call02Icon");

export const CamDuotoneIcon = createBulkRoundedIcon("Video01Icon");

export const WalletDuotoneIcon = createBulkRoundedIcon("Wallet01Icon");

export const CrownDuotoneIcon = createBulkRoundedIcon("Crown03Icon");

export const UserPlusDuotoneIcon = createBulkRoundedIcon("UserAdd02Icon");

export const BookOpenDuotoneIcon = createBulkRoundedIcon("BookOpen02Icon");

export const SquareLockIcon = createStandardIcon("SquareLock02Icon");

export const WarningSolidCircleIcon =
  createSolidStandardIcon("AlertCircleIcon");

export const WarningSolidAlertIcon = createSolidStandardIcon("Alert02Icon");

export const EditSolidIcon = createSolidStandardIcon("Edit03Icon", true);

export const ArrowDownIcon = createStandardIcon("ArrowDown01Icon");

export const UserCheckIcon = createBulkRoundedIcon("UserCheck01Icon");

export const DollarCircleIcon = createBulkRoundedIcon("DollarCircleIcon");

export const Location04Icon = createBulkRoundedIcon("Location04Icon");

export const Globe02Icon = createBulkRoundedIcon("Globe02Icon");

export const WarningBulkRoundedIcon = createBulkRoundedIcon("AlertCircleIcon");

function makeSmall(Comp: ComponentType<Icon>) {
  const SmallIcon = (props: Icon) => (
    <Comp size={12} strokeWidth={3} {...props} />
  );
  SmallIcon.displayName = `Small${Comp.displayName ?? "Icon"}`;
  return SmallIcon;
}

export const ArrowSmallLeftIcon = makeSmall(ArrowLeftIcon);
export const ArrowSmallDownIcon = makeSmall(ArrowDownIcon);
export const CrossSmallIcon = makeSmall(CrossIcon);
export const PlusSmallIcon = makeSmall(PlusIcon);
export const InfoSmallIcon = makeSmall(InfoCircleIcon);
export const CheckSmallIcon = makeSmall(CheckIcon);
export const WarningSmallIcon = makeSmall(WarningCircleIcon);
